import React, { useEffect, useRef, useState } from 'react';
import { Camera, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import RealtimeOverlayCanvas from '@/components/posture-analysis/RealtimeOverlayCanvas';
import { PostureModeProvider } from '@/components/posture-analysis/lib/PostureModeContext';
import { PRACTITIONER_STRATEGY } from '@/components/posture-analysis/lib/posture-mode-strategy';
import { DEFAULT_CAPTURE_SETTINGS } from '@/components/posture-analysis/lib/capture-settings';
import { VIEW_SEQUENCE } from '@/components/posture-analysis/constants';
import type { CaptureSequence } from '@/components/posture-analysis/types';
import { useClient } from '@/context/client-context';
import logger, { LogCategory } from '@/lib/logging';
import { cn } from '@/lib/utils';
import { useRemoteShare } from './lib/remote-share-context';
import { useLivePose } from './lib/use-live-pose';
import { servePostureHandoff } from './lib/posture-handoff';
import { REMOTE_HANDOFF_PARAM } from '@/components/posture-analysis/lib/use-remote-posture-handoff';

const STILL_QUALITY = 0.92;
/** Thumbnail frame before anything is shared: portrait posture framing. */
const DEFAULT_ASPECT = { width: 9, height: 16 };

type Still = { dataUrl: string; width: number; height: number };

function newHandoffId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Landmarks: live MediaPipe landmarks over the client on the shared window (left); views,
 * landmarks on/off, Capture pose, and captured stills on the right. Review & save opens the normal
 * Posture Analysis page in a new tab with the stills loaded (new analysis), where the practitioner
 * adjusts landmarks and saves; this tab keeps the share running.
 */
export default function RemoteLandmarksTool() {
  const { stream, tool, showLandmarks = true } = useRemoteShare();
  const { client } = useClient();
  const [video, setVideo] = useState<HTMLVideoElement | null>(null);
  const [view, setView] = useState<CaptureSequence>('Anterior');
  const [stills, setStills] = useState<Partial<Record<CaptureSequence, Still>>>({});
  // Size of the (cropped) shared window: empty thumbnail frames follow it.
  const [streamSize, setStreamSize] = useState(DEFAULT_ASPECT);
  // Detect only while this mode is on screen; hidden behind Goniometer it costs nothing.
  const liveOn = showLandmarks && !!stream && tool === 'landmarks';
  const landmarks = useLivePose(video, liveOn);

  // Latest stills for the handoff server, which lives as long as this tool.
  const stillsRef = useRef(stills);
  stillsRef.current = stills;
  const stopServingRef = useRef<Array<() => void>>([]);
  useEffect(() => () => stopServingRef.current.forEach((stop) => stop()), []);

  useEffect(() => {
    if (!video) return;
    video.srcObject = stream;
    if (stream) void video.play().catch(() => undefined);
    const readSize = () => {
      if (video.videoWidth && video.videoHeight) setStreamSize({ width: video.videoWidth, height: video.videoHeight });
    };
    readSize();
    video.addEventListener('loadedmetadata', readSize);
    video.addEventListener('resize', readSize);
    return () => {
      video.removeEventListener('loadedmetadata', readSize);
      video.removeEventListener('resize', readSize);
      video.srcObject = null;
    };
  }, [video, stream]);

  const capturePose = () => {
    if (!video || !video.videoWidth || !video.videoHeight) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg', STILL_QUALITY);
    setStills((prev) => ({ ...prev, [view]: { dataUrl, width: canvas.width, height: canvas.height } }));
    logger.info('RemoteAssessment: pose captured', { category: LogCategory.UI, view });
  };

  const reviewAndSave = () => {
    if (!client) return;
    const id = newHandoffId();
    const clientId = client.id;
    // One handoff at a time: a new Review & save replaces the previous responder.
    stopServingRef.current.forEach((stop) => stop());
    stopServingRef.current = [];
    stopServingRef.current.push(
      servePostureHandoff(id, () => ({
        clientId,
        views: Object.fromEntries(
          Object.entries(stillsRef.current).map(([v, still]) => [v, still!.dataUrl]),
        ) as Partial<Record<CaptureSequence, string>>,
      })),
    );
    window.open(`/posture-analysis?${REMOTE_HANDOFF_PARAM}=${encodeURIComponent(id)}`, '_blank');
  };

  const hasStills = Object.keys(stills).length > 0;

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 lg:flex-row">
      <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-lg border bg-muted/50">
        <video
          ref={setVideo}
          muted
          playsInline
          aria-label="Shared call window"
          className="h-full w-full object-contain"
        />
        <PostureModeProvider mode={PRACTITIONER_STRATEGY}>
          <RealtimeOverlayCanvas
            isActive={liveOn}
            landmarks={landmarks}
            currentView={view}
            captureSettings={DEFAULT_CAPTURE_SETTINGS}
            videoElement={video}
          />
        </PostureModeProvider>
      </div>

      <Card className="shrink-0 overflow-y-auto lg:w-80">
        <CardContent className="space-y-4 px-4 py-4">

          {/* The thumbnails are the view selectors: pick a view, then Capture pose fills it. */}
          <p className="text-sm font-medium">View</p>
          <p className="text-sm text-muted-foreground">
            Ask the client to turn to the selected side and stand still. Anterior is the front, Posterior the back.
          </p>
          <div className="grid grid-cols-2 justify-items-center gap-2" role="group" aria-label="View">
            {VIEW_SEQUENCE.map((v) => {
              // The frame follows the crop: a still keeps its own ratio, empty slots the live one.
              const w = stills[v]?.width ?? streamSize.width;
              const h = stills[v]?.height ?? streamSize.height;
              const portrait = h > w;
              return (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                className={cn(
                  'relative flex max-w-full items-center justify-center overflow-hidden rounded-md border bg-muted/50 text-xs text-muted-foreground',
                  // Portrait (e.g. 9:16): fixed height, width from the ratio. Landscape: full width.
                  portrait ? 'h-48 w-auto' : 'w-full',
                  view === v && 'border-primary ring-2 ring-primary',
                )}
                style={{ aspectRatio: `${w} / ${h}` }}
                aria-label={v}
                aria-pressed={view === v}
              >
                {stills[v] && (
                  <img src={stills[v]!.dataUrl} alt={`${v} still`} className="h-full w-full object-contain" />
                )}
                <span
                  className={cn(
                    'font-medium',
                    stills[v] && 'absolute inset-x-0 bottom-0 bg-background/75 py-0.5 text-center text-foreground',
                    view === v && !stills[v] && 'text-primary',
                  )}
                >
                  {v}
                </span>
              </button>
              );
            })}
          </div>

          <Button className="w-full" disabled={!stream} onClick={capturePose}>
            <Camera className="mr-2 h-4 w-4" />
            Capture pose
          </Button>

          <div className="space-y-1">
            <Button className="w-full" variant="secondary" disabled={!hasStills} onClick={reviewAndSave}>
              <ExternalLink className="mr-2 h-4 w-4" />
              Review &amp; save
            </Button>
            <p className="text-xs text-muted-foreground">
              Opens Posture Analysis in a new tab. Adjust the landmarks and save. Keep this tab open.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
